export type DateCommitValue = string | null;

const DATE_DISPLAY_PLACEHOLDER = ' / / ';

export const resolveDateBlurCommitValue = (parsedValue: string, currentValue: unknown): DateCommitValue => {
  if (parsedValue) return parsedValue;

  return null;
};

const padDatePart = (value: number) => String(value).padStart(2, '0');

const formatEditableDayPart = (digits: string) => {
  if (digits.length !== 1) return digits;
  return Number(digits) > 3 ? `0${digits}` : digits;
};

const formatEditableMonthPart = (digits: string) => {
  if (digits.length !== 1) return digits;
  return Number(digits) > 1 ? `0${digits}` : digits;
};

export const buildIsoDateValue = (day: number, month: number, year: number) => {
  const date = new Date();
  let finalYear = year;

  if (year > 9999) {
    finalYear = Number(String(year).slice(0, 4));
  }

  // Reset the day before changing month/year. Otherwise a current day such as
  // the 31st overflows months with 30 days (June, September, ...).
  date.setDate(1);
  date.setFullYear(finalYear);
  date.setMonth(month - 1);
  date.setDate(day);
  date.setHours(0, 0, 0, 0);

  const isValidDate =
    date.getFullYear() === finalYear &&
    date.getMonth() === month - 1 &&
    date.getDate() === day &&
    finalYear >= 1 &&
    finalYear <= 9999;

  return isValidDate ? `${String(finalYear).padStart(4, '0')}-${padDatePart(month)}-${padDatePart(day)}` : null;
};

export const parseDateInputValue = (nextValue: unknown) => {
  if (!nextValue) return '';

  if (nextValue instanceof Date) {
    return buildIsoDateValue(nextValue.getDate(), nextValue.getMonth() + 1, nextValue.getFullYear()) ?? '';
  }

  const rawValue = String(nextValue).trim();
  if (!rawValue || rawValue === DATE_DISPLAY_PLACEHOLDER.trim()) return '';

  let cleanValue = rawValue;
  const startsWithIso = rawValue.match(/^(\d{4,6})-(\d{1,2})-(\d{1,2})/);
  if (startsWithIso) {
    cleanValue = startsWithIso[0];
  }

  const isoMatch = cleanValue.match(/^(\d{4,6})-(\d{1,2})-(\d{1,2})$/);
  if (isoMatch) {
    return buildIsoDateValue(Number(isoMatch[3]), Number(isoMatch[2]), Number(isoMatch[1])) ?? null;
  }

  const slashOrDashMatch = rawValue.match(/^(\d{1,2})[/-](\d{1,2})[/-](\d{4,6})$/);
  if (slashOrDashMatch) {
    return buildIsoDateValue(Number(slashOrDashMatch[1]), Number(slashOrDashMatch[2]), Number(slashOrDashMatch[3]));
  }

  const compactMatch = rawValue.match(/^(\d{2})(\d{2})(\d{4})$/);
  if (compactMatch) {
    return buildIsoDateValue(Number(compactMatch[1]), Number(compactMatch[2]), Number(compactMatch[3]));
  }

  return null;
};

export const formatDateInputDisplay = (nextValue: string) => {
  if (nextValue.includes('/')) {
    const [rawDay = '', rawMonth = '', rawYear = ''] = nextValue.split('/');
    const dayDigits = rawDay.replace(/\D/g, '').slice(0, 2);
    const monthDigits = rawMonth.replace(/\D/g, '').slice(0, 2);
    const yearDigits = rawYear.replace(/\D/g, '').slice(0, 4);
    const dayPart = formatEditableDayPart(dayDigits);
    const monthPart = formatEditableMonthPart(monthDigits);

    if (!dayPart && !monthPart && !yearDigits) return DATE_DISPLAY_PLACEHOLDER;

    return `${dayPart || ' '}/${monthPart || ' '}/${yearDigits}`;
  }

  const digits = nextValue.replace(/\D/g, '').slice(0, 8);

  if (!digits) return DATE_DISPLAY_PLACEHOLDER;

  const dayPart = digits.length === 1 ? formatEditableDayPart(digits) : digits.slice(0, 2);
  const monthPart =
    digits.length <= 2 ? '' : digits.length === 3 ? formatEditableMonthPart(digits.slice(2)) : digits.slice(2, 4);
  const yearPart = digits.length <= 4 ? '' : digits.slice(4);

  return `${dayPart || ' '}/${monthPart || ' '}/${yearPart}`;
};

const countDigits = (value: string) => value.replace(/\D/g, '').length;

const getDateDisplayParts = (displayValue: string) => {
  const [dayPart = '', monthPart = '', yearPart = ''] = displayValue.split('/');
  return { dayPart, monthPart, yearPart };
};

const resolvePartCaretPosition = ({
  basePosition,
  formattedPart,
  rawDigitCountBeforeCaret,
  rawPartDigitCount,
  shouldAutoAdvancePaddedPart
}: {
  basePosition: number;
  formattedPart: string;
  rawDigitCountBeforeCaret: number;
  rawPartDigitCount: number;
  shouldAutoAdvancePaddedPart: boolean;
}) => {
  if (rawDigitCountBeforeCaret <= 0) return basePosition;

  if (shouldAutoAdvancePaddedPart) {
    return basePosition + formattedPart.length + 1;
  }

  if (rawDigitCountBeforeCaret >= 2 || rawPartDigitCount >= 2) {
    return basePosition + formattedPart.length + 1;
  }

  return basePosition + Math.min(rawDigitCountBeforeCaret, formattedPart.trim().length || 1);
};

export const resolveDateInputCaretPosition = (rawValue: string, rawCaretPosition: number, displayValue: string) => {
  const safeRawCaretPosition = Math.max(0, Math.min(rawCaretPosition, rawValue.length));
  const rawBeforeCaret = rawValue.slice(0, safeRawCaretPosition);
  const { dayPart, monthPart, yearPart } = getDateDisplayParts(displayValue);
  const dayBasePosition = 0;
  const monthBasePosition = dayPart.length + 1;
  const yearBasePosition = monthBasePosition + monthPart.length + 1;

  if (!rawValue.includes('/')) {
    const digitCountBeforeCaret = countDigits(rawBeforeCaret);

    if (digitCountBeforeCaret <= 2) {
      const rawDayDigits = rawValue.replace(/\D/g, '').slice(0, 2);
      return resolvePartCaretPosition({
        basePosition: dayBasePosition,
        formattedPart: dayPart,
        rawDigitCountBeforeCaret: digitCountBeforeCaret,
        rawPartDigitCount: rawDayDigits.length,
        shouldAutoAdvancePaddedPart: rawDayDigits.length === 1 && dayPart.length === 2
      });
    }

    if (digitCountBeforeCaret <= 4) {
      const rawMonthDigits = rawValue.replace(/\D/g, '').slice(2, 4);
      return resolvePartCaretPosition({
        basePosition: monthBasePosition,
        formattedPart: monthPart,
        rawDigitCountBeforeCaret: digitCountBeforeCaret - 2,
        rawPartDigitCount: rawMonthDigits.length,
        shouldAutoAdvancePaddedPart: rawMonthDigits.length === 1 && monthPart.length === 2
      });
    }

    return Math.min(yearBasePosition + digitCountBeforeCaret - 4, yearBasePosition + yearPart.length);
  }

  const rawParts = rawValue.split('/');
  const rawPartIndex = rawBeforeCaret.split('/').length - 1;

  if (rawPartIndex <= 0) {
    const rawDayBeforeCaret = rawParts[0]?.slice(0, rawBeforeCaret.length) ?? '';
    const rawDayDigits = rawParts[0]?.replace(/\D/g, '').slice(0, 2) ?? '';
    const rawDayDigitsBeforeCaret = countDigits(rawDayBeforeCaret);

    return resolvePartCaretPosition({
      basePosition: dayBasePosition,
      formattedPart: dayPart,
      rawDigitCountBeforeCaret: rawDayDigitsBeforeCaret,
      rawPartDigitCount: rawDayDigits.length,
      shouldAutoAdvancePaddedPart: rawDayDigits.length === 1 && dayPart.length === 2
    });
  }

  if (rawPartIndex === 1) {
    const rawMonthBeforeCaret = rawBeforeCaret.split('/')[1] ?? '';
    const rawMonthDigits = rawParts[1]?.replace(/\D/g, '').slice(0, 2) ?? '';
    const rawMonthDigitsBeforeCaret = countDigits(rawMonthBeforeCaret);

    return resolvePartCaretPosition({
      basePosition: monthBasePosition,
      formattedPart: monthPart,
      rawDigitCountBeforeCaret: rawMonthDigitsBeforeCaret,
      rawPartDigitCount: rawMonthDigits.length,
      shouldAutoAdvancePaddedPart: rawMonthDigits.length === 1 && monthPart.length === 2
    });
  }

  const rawYearBeforeCaret = rawBeforeCaret.split('/').slice(2).join('/');
  return Math.min(yearBasePosition + countDigits(rawYearBeforeCaret), yearBasePosition + yearPart.length);
};

export const formatIsoDateDisplay = (isoValue: string) => {
  const match = isoValue.match(/^(\d{4})-(\d{2})-(\d{2})$/);
  return match ? `${match[3]}/${match[2]}/${match[1]}` : DATE_DISPLAY_PLACEHOLDER;
};

export const formatDateFieldDisplay = (nextValue: unknown) => {
  const parsedValue = parseDateInputValue(nextValue);
  return parsedValue ? formatIsoDateDisplay(parsedValue) : DATE_DISPLAY_PLACEHOLDER;
};

export const resolveDateEnterCommitValue = (displayValue: string, currentDate = new Date()) => {
  const digits = displayValue.replace(/\D/g, '').slice(0, 8);

  if (!digits) return '';

  const day = Number(digits.length === 1 ? `0${digits}` : digits.slice(0, 2));
  const month =
    digits.length >= 4
      ? Number(digits.slice(2, 4))
      : digits.length === 3
        ? Number(`0${digits.slice(2)}`)
        : currentDate.getMonth() + 1;
  const year = digits.length >= 8 ? Number(digits.slice(4, 8)) : currentDate.getFullYear();

  return buildIsoDateValue(day, month, year) ?? '';
};

export const isEmptyDateDisplay = (displayValue: string) => displayValue === DATE_DISPLAY_PLACEHOLDER;

export { DATE_DISPLAY_PLACEHOLDER };
